// Tiptap mention suggestion driver for the conversation editor.
// Talks to /api/tools/production-schedule/users so the picker
// surfaces app users (Entra-authenticated) rather than the workshop
// team-member lookup table. Selecting a row inserts a
// `<span data-type="mention" data-id="UUID" data-label="Name">@Name</span>`
// node where `data-id` is the planner_user uuid — the same identity
// the Team Planner notification fan-out reads from
// payload.mentions[], so the dispatcher routes email + Teams
// notifications without an identity translation.
//
// Graph-only hits (people in the tenant who haven't signed into the
// planner yet) come back with pendingProjection: true; the driver
// posts to /projection on selection to materialise their
// planner_user row, then inserts the new uuid into the editor. The
// notification dispatcher requires a planner_user row to look up
// preferences + the Teams conversation reference, so the projection
// has to happen before the mention is committed.
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { MentionNodeAttrs } from "@tiptap/extension-mention";
import {
  projectAppUser,
  searchAppUsers,
  type AppUserPickerRow,
} from "../hooks/useProductionApi";

interface ListProps {
  items: AppUserPickerRow[];
  loading: boolean;
  command: (item: MentionNodeAttrs) => void;
  active: number;
  onPick: (row: AppUserPickerRow) => Promise<void>;
}

function initials(displayName: string | null, email: string | null): string {
  const src = displayName ?? email ?? "?";
  const parts = src.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

function renderList(host: HTMLElement, props: ListProps) {
  host.innerHTML = "";
  if (props.loading) {
    const empty = document.createElement("div");
    empty.className = "bsp-mention-empty";
    empty.textContent = "Searching…";
    host.appendChild(empty);
    return;
  }
  if (props.items.length === 0) {
    const empty = document.createElement("div");
    empty.className = "bsp-mention-empty";
    empty.textContent = "No matching users";
    host.appendChild(empty);
    return;
  }
  for (let i = 0; i < props.items.length; i += 1) {
    const u = props.items[i]!;
    const row = document.createElement("button");
    row.type = "button";
    row.className = `bsp-mention-item${i === props.active ? " is-active" : ""}`;
    row.dataset.id = u.id;
    const avatar = document.createElement("span");
    avatar.className = "bsp-mention-avatar";
    // Graph-only hits get a slightly different swatch so the user
    // can tell at a glance which results need a one-shot
    // projection round-trip.
    avatar.style.background = u.pendingProjection ? "#9CA09F" : "#1B449C";
    avatar.textContent = initials(u.displayName, u.email);
    const labels = document.createElement("span");
    labels.className = "bsp-mention-labels";
    const name = document.createElement("strong");
    name.textContent = u.displayName ?? u.email ?? "Unnamed";
    const role = document.createElement("em");
    role.textContent = u.email ?? "";
    labels.appendChild(name);
    labels.appendChild(role);
    row.appendChild(avatar);
    row.appendChild(labels);
    row.addEventListener("mousedown", (e) => {
      e.preventDefault();
      void props.onPick(u);
    });
    host.appendChild(row);
  }
}

export function makeMentionSuggestion(): Omit<
  SuggestionOptions<AppUserPickerRow, MentionNodeAttrs>,
  "editor"
> {
  // The suggestion driver doesn't have access to React state, so a
  // module-scoped cache short-circuits repeated identical fetches
  // while the picker is open (every keystroke calls items()).
  const cache = new Map<string, AppUserPickerRow[]>();
  let lastQuery = "";
  return {
    char: "@",
    allowSpaces: false,
    startOfLine: false,
    items: async ({ query }) => {
      const key = query.toLowerCase();
      lastQuery = key;
      if (cache.has(key)) return cache.get(key)!;
      try {
        const rows = await searchAppUsers(query);
        // Ignore stale responses if the user kept typing while we
        // were in flight (race-condition guard: only the most
        // recent query renders).
        if (key !== lastQuery) return [];
        cache.set(key, rows.slice(0, 8));
        return cache.get(key)!;
      } catch (err) {
        console.error("[mention] search failed", err);
        return [];
      }
    },
    render: () => {
      let host: HTMLDivElement | null = null;
      let items: AppUserPickerRow[] = [];
      let active = 0;
      let loading = false;
      let command: ((item: MentionNodeAttrs) => void) | null = null;
      let clientRect: (() => DOMRect | null) | null | undefined = null;

      const place = () => {
        if (!host || !clientRect) return;
        const rect = clientRect();
        if (!rect) {
          host.style.display = "none";
          return;
        }
        host.style.display = "block";
        host.style.top = `${rect.bottom + window.scrollY + 4}px`;
        host.style.left = `${rect.left + window.scrollX}px`;
      };

      // Commit a pick — if the row is still a Graph-only hit, project
      // it first so the mention's data-id is a planner_user uuid the
      // notifier can resolve.
      const onPick = async (row: AppUserPickerRow) => {
        if (!command) return;
        let resolved = row;
        if (row.pendingProjection) {
          try {
            resolved = await projectAppUser(row);
          } catch (err) {
            console.error("[mention] projection failed", err);
            return;
          }
        }
        command({
          id: resolved.id,
          label: resolved.displayName ?? resolved.email ?? "Unnamed",
        });
      };

      const refresh = () => {
        if (!host) return;
        renderList(host, { items, command: command!, active, loading, onPick });
      };

      return {
        onStart: (props) => {
          host = document.createElement("div");
          host.className = "bsp-mention-pop";
          document.body.appendChild(host);
          items = props.items;
          active = 0;
          command = props.command;
          clientRect = props.clientRect;
          loading = false;
          refresh();
          place();
        },
        onUpdate: (props) => {
          items = props.items;
          active = 0;
          command = props.command;
          clientRect = props.clientRect;
          loading = false;
          refresh();
          place();
        },
        onKeyDown: (props) => {
          if (props.event.key === "ArrowDown") {
            active = (active + 1) % Math.max(items.length, 1);
            refresh();
            return true;
          }
          if (props.event.key === "ArrowUp") {
            active = (active - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1);
            refresh();
            return true;
          }
          if (props.event.key === "Enter" || props.event.key === "Tab") {
            if (items.length === 0) return false;
            const pick = items[active] ?? items[0];
            if (!pick) return false;
            void onPick(pick);
            return true;
          }
          if (props.event.key === "Escape") {
            if (host) host.style.display = "none";
            return true;
          }
          return false;
        },
        onExit: () => {
          if (host && host.parentNode) host.parentNode.removeChild(host);
          host = null;
          command = null;
        },
      };
    },
  };
}
